// Shared Modal Component
// Design: Black (#000), Bone (#fffbf7), Sage (#a1c6a2)
import React, { useEffect, useRef } from 'react';
import useKeyboardShortcuts from '../../hooks/useKeyboardShortcuts';

const Modal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  children,
  footer,
  maxWidth = 'sm:max-w-lg'
}) => {
  const modalRef = useRef(null);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    'Escape': () => onClose(),
  }, isOpen);

  // Focus trap
  useEffect(() => {
    if (isOpen) {
      const firstInput = modalRef.current?.querySelector('input, select, textarea, button');
      firstInput?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-80 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div
          ref={modalRef}
          className={`relative inline-block align-bottom bg-[#0a0a0a] border border-[rgba(255,251,247,0.1)] text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle ${maxWidth} sm:w-full`}
        >
          {/* Modal Header */}
          <div className="border-b border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {icon && (
                  <div className="w-8 h-8 border border-[#a1c6a2] flex items-center justify-center">
                    {icon}
                  </div>
                )}
                <div>
                  {subtitle && (
                    <span
                      className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.2em] uppercase block"
                      style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                    >
                      {subtitle}
                    </span>
                  )}
                  <h3 className="text-lg font-semibold text-[#fffbf7] tracking-[0.05em] uppercase">
                    {title}
                  </h3>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-[rgba(255,251,247,0.4)] hover:text-[#fffbf7] transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="px-6 py-6">
            {children}
          </div>

          {/* Modal Footer */}
          {footer && (
            <div className="border-t border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black flex flex-row-reverse gap-3">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper components for consistent form styling
export const ModalFormField = ({ label, required, children }) => (
  <div className="mb-5">
    <label
      className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.2em] uppercase block mb-2"
      style={{ fontFamily: "'GT Pressura Mono', monospace" }}
    >
      {label} {required && <span className="text-[#c47474]">*</span>}
    </label>
    {children}
  </div>
);

export const ModalInput = ({ ...props }) => (
  <input
    {...props}
    className="w-full px-4 py-3 border border-[rgba(255,251,247,0.2)] bg-black text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2] transition-colors placeholder:text-[rgba(255,251,247,0.3)]"
  />
);

export const ModalSelect = ({ children, ...props }) => (
  <select
    {...props}
    className="w-full px-4 py-3 border border-[rgba(255,251,247,0.2)] bg-black text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2] transition-colors disabled:opacity-50"
  >
    {children}
  </select>
);

export const ModalTextarea = ({ ...props }) => (
  <textarea
    {...props}
    className="w-full px-4 py-3 border border-[rgba(255,251,247,0.2)] bg-black text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2] transition-colors placeholder:text-[rgba(255,251,247,0.3)] resize-none"
  />
);

export const ModalPrimaryButton = ({ children, loading, ...props }) => (
  <button
    {...props}
    className="px-6 py-3 bg-[#fffbf7] text-black text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#a1c6a2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
  >
    {loading ? 'Loading...' : children}
  </button>
);

export const ModalSecondaryButton = ({ children, ...props }) => (
  <button
    {...props}
    className="px-6 py-3 border border-[rgba(255,251,247,0.2)] text-[rgba(255,251,247,0.6)] text-xs font-semibold tracking-[0.15em] uppercase hover:text-[#fffbf7] hover:border-[rgba(255,251,247,0.4)] transition-colors"
    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
  >
    {children}
  </button>
);

export const ModalDangerButton = ({ children, loading, ...props }) => (
  <button
    {...props}
    className="px-6 py-3 bg-[#c47474] text-white text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#a85454] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
  >
    {loading ? 'Loading...' : children}
  </button>
);

export default Modal;
